import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  computeTenantStorageUsedBytes,
  refreshTenantStorageUsageCache,
  getTenantStorageLimitMb,
  formatStorageBytes,
  formatStorageMb,
  storageUsagePercent,
} from '../../utils/consultorioStorageQuota';

function statusMeta(pct) {
  if (pct >= 95) return { variant: 'danger', label: 'Casi lleno', tone: 'critical' };
  if (pct >= 80) return { variant: 'warning', label: 'Alto', tone: 'warn' };
  return { variant: 'success', label: 'Disponible', tone: 'ok' };
}

export default function ConsultorioStorageBar({
  tenantId,
  limitMb,
  memberDoctorIds = [],
  className = '',
  variant = 'default',
}) {
  const [usedBytes, setUsedBytes] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!tenantId) {
      setUsedBytes(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const used = await refreshTenantStorageUsageCache(tenantId, memberDoctorIds);
      setUsedBytes(used);
    } catch {
      setUsedBytes(0);
    } finally {
      setLoading(false);
    }
  }, [tenantId, memberDoctorIds]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onStorage = () => refresh();
    window.addEventListener('consultorio-storage-changed', onStorage);
    return () => window.removeEventListener('consultorio-storage-changed', onStorage);
  }, [refresh]);

  if (!tenantId) return null;

  const limMb = limitMb ?? getTenantStorageLimitMb(tenantId);
  const limitBytes = limMb * 1024 * 1024;
  const pct = storageUsagePercent(usedBytes, limitBytes);
  const { variant: barVariant, label: statusLabel, tone } = statusMeta(pct);
  const usedLabel = loading ? '…' : formatStorageBytes(usedBytes);
  const limitLabel = formatStorageMb(limMb);
  const rootClass = [
    'consultorio-storage',
    `consultorio-storage--${variant}`,
    tone !== 'ok' ? `consultorio-storage--${tone}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const renderGlobalDetails = () => (
    <>
      <div className="consultorio-storage__global-card">
        <div className="consultorio-storage__global-accent" aria-hidden />
        <span className="consultorio-storage__icon consultorio-storage__icon--global" aria-hidden>
          <i className="bi bi-hdd-stack" />
        </span>
        <div className="consultorio-storage__global-body">
          <div className="consultorio-storage__global-head">
            <div className="consultorio-storage__global-titles">
              <span className="consultorio-storage__title consultorio-storage__title--global">
                Almacenamiento clínico
              </span>
              <span className="consultorio-storage__subtitle consultorio-storage__subtitle--global">
                Radiografías, consentimientos firmados e imágenes del consultorio
              </span>
            </div>
            <div className="consultorio-storage__global-meta">
              <span className={`consultorio-storage__status-chip consultorio-storage__status-chip--${barVariant}`}>
                {loading ? 'Calculando…' : statusLabel}
              </span>
              <span className="consultorio-storage__values consultorio-storage__values--global">
                <span className="consultorio-storage__value-used">{usedLabel}</span>
                <span className="consultorio-storage__sep">de</span>
                <span className="consultorio-storage__value-limit">{limitLabel}</span>
              </span>
            </div>
          </div>
          <div className="consultorio-storage__track consultorio-storage__track--global">
            <div
              className={`consultorio-storage__fill consultorio-storage__fill--${barVariant}${loading ? ' consultorio-storage__fill--loading' : ''}`}
              style={{ width: `${Math.max(pct, loading ? 8 : 2)}%` }}
            />
          </div>
        </div>
        <div className={`consultorio-storage__global-pct consultorio-storage__global-pct--${barVariant}`}>
          <span className="consultorio-storage__global-pct-num">{loading ? '—' : `${pct}%`}</span>
          <span className="consultorio-storage__global-pct-label">en uso</span>
        </div>
      </div>
      {!loading && pct >= 80 ? (
        <p className={`consultorio-storage__global-hint consultorio-storage__global-hint--${barVariant}`}>
          <i className="bi bi-exclamation-triangle-fill" aria-hidden />
          {pct >= 95
            ? 'Espacio casi agotado. Elimine estudios antiguos o solicite ampliar la cuota.'
            : 'Se está acercando al límite de almacenamiento.'}
        </p>
      ) : null}
    </>
  );

  if (variant === 'floating') {
    return (
      <FloatingStorageWidget
        rootClass={rootClass}
        barVariant={barVariant}
        loading={loading}
        statusLabel={statusLabel}
        usedLabel={usedLabel}
        limitLabel={limitLabel}
        pct={pct}
        renderDetails={renderGlobalDetails}
      />
    );
  }

  if (variant === 'global') {
    return (
      <div className={rootClass} role="status" aria-label="Uso de almacenamiento clínico">
        {renderGlobalDetails()}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={rootClass} role="status" aria-label="Uso de almacenamiento clínico">
        <div className="consultorio-storage__compact-inner">
          <span className="consultorio-storage__icon" aria-hidden>
            <i className="bi bi-images" />
          </span>
          <div className="consultorio-storage__compact-main">
            <div className="consultorio-storage__compact-top">
              <span className="consultorio-storage__title">Estudios e imágenes</span>
              <span className="consultorio-storage__values">
                {usedLabel}
                <span className="consultorio-storage__sep">/</span>
                {limitLabel}
              </span>
            </div>
            <div className="consultorio-storage__track">
              <div
                className={`consultorio-storage__fill consultorio-storage__fill--${barVariant}`}
                style={{ width: `${Math.max(pct, loading ? 0 : 2)}%` }}
              />
            </div>
          </div>
          <span className={`consultorio-storage__pct consultorio-storage__pct--${barVariant}`}>
            {loading ? '—' : `${pct}%`}
          </span>
        </div>
      </div>
    );
  }

  if (variant === 'admin') {
    return (
      <div className={rootClass} role="status">
        <span className="admin-consultorios-detail-stat-icon admin-consultorios-detail-stat-icon--storage" aria-hidden>
          <i className="bi bi-hdd-stack" />
        </span>
        <div className="consultorio-storage__admin-body">
          <div className="consultorio-storage__admin-row">
            <span className="consultorio-storage__admin-label">Radiografías e imágenes</span>
            <span className="consultorio-storage__admin-values">
              {usedLabel}
              <span className="consultorio-storage__sep">/</span>
              {limitLabel}
            </span>
          </div>
          <div className="progress consultorio-storage__admin-progress">
            <div
              className={`progress-bar bg-${barVariant}`}
              style={{ width: `${Math.max(pct, loading ? 0 : 3)}%` }}
            />
          </div>
          <span className="consultorio-storage__admin-hint">
            {loading ? 'Calculando…' : `${pct}% · estimado en este navegador`}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass} role="status" aria-label="Almacenamiento de radiografías e imágenes">
      <div className="consultorio-storage__header">
        <div className="consultorio-storage__brand">
          <span className="consultorio-storage__icon consultorio-storage__icon--lg" aria-hidden>
            <i className="bi bi-hdd-stack" />
          </span>
          <div>
            <div className="consultorio-storage__title">Almacenamiento clínico</div>
            <div className="consultorio-storage__subtitle">Radiografías e imágenes</div>
          </div>
        </div>
        <div className="consultorio-storage__aside">
          <span className={`consultorio-storage__badge consultorio-storage__badge--${barVariant}`}>
            {loading ? '…' : statusLabel}
          </span>
          <div className="consultorio-storage__values consultorio-storage__values--lg">
            {usedLabel}
            <span className="consultorio-storage__sep">/</span>
            {limitLabel}
          </div>
        </div>
      </div>
      <div className="consultorio-storage__track consultorio-storage__track--lg">
        <div
          className={`consultorio-storage__fill consultorio-storage__fill--${barVariant}`}
          style={{ width: `${Math.max(pct, loading ? 0 : 2)}%` }}
        />
      </div>
      {!loading && pct >= 80 ? (
        <p className="consultorio-storage__alert">
          {pct >= 95
            ? 'Espacio casi agotado. Elimine estudios antiguos o solicite ampliar la cuota.'
            : 'Se está acercando al límite de almacenamiento.'}
        </p>
      ) : null}
    </div>
  );
}

function FloatingStorageWidget({
  rootClass,
  barVariant,
  loading,
  statusLabel,
  usedLabel,
  limitLabel,
  pct,
  renderDetails,
}) {
  const [expanded, setExpanded] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!expanded) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [expanded]);

  return (
    <div
      ref={wrapRef}
      className={`${rootClass} consultorio-storage-float`}
      data-expanded={expanded ? 'true' : 'false'}
    >
      {expanded ? (
        <div
          id="consultorio-storage-float-panel"
          className="consultorio-storage-float__panel"
          role="region"
          aria-label="Detalle de almacenamiento clínico"
        >
          {renderDetails()}
        </div>
      ) : null}

      <button
        type="button"
        className={`consultorio-storage-float__chip consultorio-storage-float__chip--${barVariant}`}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="consultorio-storage-float-panel"
        aria-label="Almacenamiento clínico. Clic para ver detalle."
      >
        <span className="consultorio-storage-float__chip-icon" aria-hidden>
          <i className="bi bi-hdd-stack" />
        </span>
        <span className="consultorio-storage-float__chip-text">
          <span className="consultorio-storage-float__chip-title">Almacenamiento</span>
          <span className="consultorio-storage-float__chip-meta">
            {loading ? '…' : `${usedLabel} / ${limitLabel}`}
          </span>
        </span>
        <span className="consultorio-storage-float__chip-track" aria-hidden>
          <span
            className={`consultorio-storage-float__chip-fill consultorio-storage-float__chip-fill--${barVariant}`}
            style={{ width: `${Math.max(pct, loading ? 6 : 2)}%` }}
          />
        </span>
        <span className={`consultorio-storage-float__chip-pct consultorio-storage-float__chip-pct--${barVariant}`}>
          {loading ? '—' : `${pct}%`}
        </span>
        <span className="consultorio-storage-float__chip-chevron" aria-hidden>
          <i className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-up'}`} />
        </span>
      </button>
    </div>
  );
}

export function notifyConsultorioStorageChanged() {
  window.dispatchEvent(new Event('consultorio-storage-changed'));
}
